// react
import { MouseEvent, useContext, useMemo, useState } from 'react';

// types
import {
	Account,
	ActionButton,
	Entity,
	ErrorCause,
	Feedback,
	Transaction,
	TransactionStatus,
	TransactionType,
} from '../../../@types';

// router
import { useSearchParams } from 'react-router-dom';

// i18n
import { Trans, useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, AlertTitle, Box, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';

// ui
import { AlertFeedback, AlertFeedbackProps } from '../../ui/AlertFeedback/AlertFeedback';
import { BoxKeyValue } from '../../ui/BoxKeyValue/BoxKeyValue';
import { entityIconTypes, IconCircle, transactionIconTypes } from '../../ui/IconCircle/IconCircle';
import { Loading } from '../../ui/Loading/Loading';

// fields
import { Input } from '../../fields/Input/Input';

// utils
import { formatCurrency } from '../../../utils/currency';
import { translateError, translateType } from '../../../utils/translations';

// props
export interface DialogUpdateTransactionStatusProps {
	isOpen: boolean;
	onClose: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	onSuccess?: () => void;
	status: TransactionStatus;
	type: TransactionType;
}

export const DialogUpdateTransactionStatus = ({
	isOpen,
	onClose,
	onSuccess,
	status,
	type,
}: DialogUpdateTransactionStatusProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialogTransaction = searchParams.get('dialogTransaction');

	// context
	const { setBanner } = useContext(ContextBanner);

	// state
	const [feedback, setFeedback] = useState<AlertFeedbackProps | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [transaction, setTransaction] = useState<Transaction | null>(null);

	// vars
	const idForm = 'form-update-transaction-status';
	const isOpEx = type === 'OPEX';
	const isTransfer = type === 'TRANSFER';

	// deactivate translations
	const dialog = {
		alert: t('alert.revertTransaction'),
		btnSubmit: {
			children: t('revertPayment'),
			color: 'error',
		},
		i18nKey: 'dialogUpdateTransactionStatus.revert.content',
		title: t('revertPayment'),
	};

	if (type === 'CASH_BENEFIT') {
		dialog.btnSubmit = {
			children: t('revertCashBenefit'),
			color: 'error',
		};
		dialog.title = t('revertCashBenefit');
	}

	if (isOpEx) {
		dialog.alert = t('alert.revertOpEx');
		dialog.btnSubmit = {
			children: t('revertOpEx'),
			color: 'error',
		};
		dialog.i18nKey = 'dialogUpdateTransactionStatus.revertOpEx.content';
		dialog.title = t('revertOpEx');
	}

	if (isTransfer) {
		dialog.alert = t('alert.revertTransfer');
		dialog.btnSubmit = {
			children: t('revertTransfer'),
			color: 'error',
		};
		dialog.i18nKey = 'dialogUpdateTransactionStatus.revertTransfer.content';
		dialog.title = t('revertTransfer');
	}

	const amountFormatted = formatCurrency({
		currency: transaction?.currency,
		value: Number((transaction?.amount || 0) + (transaction?.tax || 0)),
	});

	const fromAccount = transaction?.fromAccount as Account;
	const toAccount = transaction?.toAccount as Account;
	const fromEntity = transaction?.fromEntity as Entity;
	const toEntity = transaction?.toEntity as Entity;

	// memoized entity and account components
	const boxToEntity = useMemo(() => {
		if (!toEntity) return null;
		return (
			<BoxKeyValue
				BlockchainHashProps={{
					hash: toEntity.wallet?.address,
				}}
				GridProps={{
					alignItems: 'flex-start',
					flexDirection: {
						xs: 'row-reverse',
						md: 'row',
					},
				}}
				IconCircleProps={{
					type: entityIconTypes[toEntity.type],
				}}
				label={{
					children: translateType(toEntity.type, t),
				}}
				value={{
					children: toEntity.name,
					sx: {
						overflow: 'hidden',
						textOverflow: 'ellipsis',
					},
				}}
			/>
		);
	}, [toEntity, t]);

	const boxFromEntity = useMemo(() => {
		if (!fromEntity) return null;
		return (
			<BoxKeyValue
				BlockchainHashProps={{
					hash: fromEntity.wallet?.address,
				}}
				GridProps={{
					alignItems: 'flex-start',
					flexDirection: {
						xs: 'row-reverse',
						md: 'row',
					},
				}}
				IconCircleProps={{
					type: entityIconTypes[fromEntity.type],
				}}
				label={{
					children: translateType(fromEntity.type, t),
				}}
				value={{
					children: fromEntity.name,
					sx: {
						overflow: 'hidden',
						textOverflow: 'ellipsis',
					},
				}}
			/>
		);
	}, [fromEntity, t]);

	const boxFromAccount = useMemo(() => {
		if (!fromAccount) return null;
		return (
			<BoxKeyValue
				GridProps={{
					alignItems: {
						xs: 'center',
						lg: isOpEx ? 'flex-start' : 'center',
					},
					flexDirection: {
						xs: 'row-reverse',
						md: 'row',
					},
				}}
				IconCircleProps={{
					type: 'account',
				}}
				label={{
					// original fromAccount now receives funds
					children: t('receivingAccount'),
				}}
				value={{
					children: fromAccount.name,
				}}
				sxBox={{
					display: 'flex',
					alignItems: isOpEx ? 'flex-start' : 'center',
				}}
			/>
		);
	}, [fromAccount, isOpEx, t]);

	const boxToAccount = useMemo(() => {
		if (!toAccount) return null;
		return (
			<BoxKeyValue
				GridProps={{
					alignItems: {
						xs: 'center',
						lg: 'center',
					},
					flexDirection: {
						xs: 'row-reverse',
						md: 'row',
					},
				}}
				IconCircleProps={{
					type: 'account',
				}}
				label={{
					// original toAccount now sends funds
					children: t('sendingAccount'),
				}}
				value={{
					children: toAccount.name,
				}}
				sxBox={{
					display: 'flex',
					alignItems: 'center',
				}}
			/>
		);
	}, [toAccount, t]);

	// fetch transaction
	const { isLoading: isLoadingTransaction } = useFetch({
		isEnabled: Boolean(paramDialogTransaction && isOpen),
		url: `${process.env.REACT_APP_API_URL}/transactions/${paramDialogTransaction}?populate=fromAccount,fromEntity,toAccount,toEntity`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setTransaction(res.data[0]);
			}
		},
	});

	// handlers
	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		const feedbackNew: AlertFeedbackProps & { items: Feedback[] } = {
			items: [],
		};

		try {
			// set loader
			setIsLoading(true);

			// clear feedback
			setFeedback(null);

			const fetchPatchTransaction = await fetch(
				`${process.env.REACT_APP_API_URL}/transactions/${paramDialogTransaction}`,
				{
					method: 'PATCH',
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
					body: JSON.stringify({
						status,
					}),
				}
			);

			const resPatchTransaction = await fetchPatchTransaction.json();

			if (resPatchTransaction.error) {
				throw new Error(resPatchTransaction.error.message, {
					cause: {
						id: resPatchTransaction.error.id,
					},
				});
			}

			// post comments
			if (entries.comments) {
				const fetchPostComment = await fetch(`${process.env.REACT_APP_API_URL}/comments`, {
					method: 'post',
					body: JSON.stringify({
						comment: entries.comments,
						transaction: Number(paramDialogTransaction),
					}),
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				});

				const resPostComment = await fetchPostComment.json();

				if (resPostComment.error) {
					feedbackNew.items.push({
						alert: {
							severity: 'error',
						},
						response: resPostComment,
					});
				}
			}

			if (onSuccess) {
				onSuccess();
			}

			let message = t('alert.transactionReverted', {
				amount: amountFormatted,
				toEntity: toEntity?.name,
			});

			if (isOpEx) {
				message = t('alert.transactionOpExReverted', {
					amount: amountFormatted,
				});
			}
			if (isTransfer) {
				message = t('alert.transactionTransferReverted', {
					amount: amountFormatted,
				});
			}

			feedbackNew.items.push({
				alert: {
					children: message,
					severity: 'success',
				},
			});

			if (!feedbackNew.items.some((i) => i.alert.severity === 'error')) {
				// set success banner
				setBanner({
					hasClose: true,
					message: message,
					severity: 'success',
				});

				// close dialog
				if (onClose) {
					onClose();
				}
			} else {
				setFeedback(feedbackNew);
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			feedbackNew.items.push({
				alert: {
					children: translateError({ cause, t }),
					severity: 'error',
				},
			});

			// set error
			setFeedback(feedbackNew);
		} finally {
			setIsLoading(false);
		}
	};

	const handleClose = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear error
		setFeedback(null);

		if (onClose) {
			onClose(e);
		}
	};

	const renderContent = () => {
		if (isLoadingTransaction) {
			return (
				<Grid item xs={12}>
					<Grid alignItems="center" container justifyContent="center">
						<Grid item>
							<Loading />
						</Grid>
					</Grid>
				</Grid>
			);
		}

		const arrowRight = (
			<Box
				sx={(theme) => {
					return {
						transform: 'translate(-50%, -50%)',
						position: 'absolute',
						top: {
							xs: 'calc(50% + 8px)',
							md: '50%',
						},
						left: '50%',
						'& svg': {
							transform: `rotate(90deg)`,
							[theme.breakpoints.up('md')]: {
								transform: `rotate(0deg)`,
							},
						},
					};
				}}>
				<IconCircle color="grey" size="sm" type="arrowForward" />
			</Box>
		);

		return (
			<Grid item xs={12}>
				<form id={idForm} onSubmit={handleSubmit}>
					<Grid container spacing={2}>
						<Grid item xs={12}>
							<Trans
								components={{
									strong: <Typography color="brand.blue.600" component="span" fontWeight={600} />,
								}}
								i18nKey={dialog.i18nKey}
								values={{
									amount: amountFormatted,
									fromEntity: fromEntity?.name,
									toEntity: toEntity?.name,
								}}
							/>
						</Grid>
						{status === 'REVERTED' && (
							<Grid item xs={12}>
								<Grid container spacing={{ xs: 2, md: 1 }}>
									{type !== 'OPEX' && type !== 'TRANSFER' && (
										<>
											<Grid item xs={12}>
												<Box
													sx={(theme) => {
														return {
															position: 'relative',
														};
													}}>
													<Grid container spacing={{ xs: 2, md: 1 }}>
														<Grid item xs={12} md={6}>
															{boxToEntity}
														</Grid>
														<Grid item xs={12} md={6}>
															{boxFromEntity}
														</Grid>
													</Grid>
													{arrowRight}
												</Box>
											</Grid>
											<Grid item xs={12}>
												<Grid item xs={12}>
													{boxFromAccount}
												</Grid>
											</Grid>
										</>
									)}
									{isOpEx && (
										<Grid item xs={12}>
											<Grid container spacing={{ xs: 2, md: 1 }} alignItems="stretch">
												<Grid item xs={12}>
													{boxFromEntity}
												</Grid>
												<Grid item xs={12}>
													{boxFromAccount}
												</Grid>
											</Grid>
										</Grid>
									)}
									{type === 'TRANSFER' && (
										<Grid item xs={12}>
											<Box
												sx={(theme) => {
													return {
														position: 'relative',
													};
												}}>
												<Grid container spacing={{ xs: 2, md: 1 }} alignItems="stretch">
													<Grid item xs={12} md={6}>
														{boxToAccount}
													</Grid>
													<Grid item xs={12} md={6}>
														{boxFromAccount}
													</Grid>
													{arrowRight}
												</Grid>
											</Box>
										</Grid>
									)}
								</Grid>
							</Grid>
						)}

						<Grid item xs={12}>
							<Alert severity="warning">
								<AlertTitle>{t('thisCannotBeUndone')}</AlertTitle>
								{dialog.alert}
							</Alert>
						</Grid>
						<Grid item xs={12}>
							<Input
								id={`${idForm}-comments`}
								label={t('comments')}
								minRows={2}
								multiline={true}
								name="comments"
							/>
						</Grid>
						{feedback && (
							<Grid item xs={12}>
								<AlertFeedback {...feedback} />
							</Grid>
						)}
					</Grid>
				</form>
			</Grid>
		);
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: handleClose,
					variant: 'text',
				},
				{
					...(dialog.btnSubmit as ActionButton),
					form: idForm,
					loading: isLoading,
					variant: 'contained',
					type: 'submit',
				},
			]}
			alignItems="center"
			IconCircleProps={{
				type: transactionIconTypes[type],
			}}
			isOpen={isOpen}
			maxWidth={500}
			onClose={handleClose}
			subtitle={{
				children: isLoadingTransaction ? undefined : `${amountFormatted}`,
			}}
			title={{
				children: dialog.title,
			}}>
			<Grid container spacing={2}>
				{renderContent()}
			</Grid>
		</DialogActions>
	);
};
